// The 3D dice overlay: a single transparent, click-through canvas over the current surface. It listens on the dice bus
// (dice-bus.ts) and animates a roll the RNG has ALREADY produced, landing each die on its rolled face, then fading it
// out over the board. `three` is dynamic-imported on the first roll so it stays out of first paint (like the rules
// engine). Honours prefers-reduced-motion (then it never mounts the canvas — the roll feed still shows every result).
import { useEffect, useRef, useState } from "react";
import { onDiceThrow, type DiceThrow } from "../lib/dice-bus";
import { DICE_STYLES, readDiceStyle, type DiceStyle } from "../lib/dice-style";

type THREE = typeof import("three");
type V3 = import("three").Vector3;

/**
 * The number colours for a die body: a fill and a contrasting outline, so the pip reads on any colour (owner: "the
 * number color must always be high contrast to the actual die color"). Light-enough bodies (bone) get dark numbers;
 * everything from gold on down gets WHITE numbers (owner: "if the die is gold color the text should be white"). The
 * outline is the opposite, so even white-on-gold stays legible.
 */
function inkPair(bodyHex: number): { fill: string; outline: string } {
  const lin = (c: number) => { c /= 255; return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4); };
  const lum = 0.2126 * lin((bodyHex >> 16) & 255) + 0.7152 * lin((bodyHex >> 8) & 255) + 0.0722 * lin(bodyHex & 255);
  return lum > 0.6 ? { fill: "#15100a", outline: "rgba(255,255,255,0.7)" } : { fill: "#fdf6e6", outline: "rgba(0,0,0,0.6)" };
}
const HI_GLOW = "#ffd070";   // the winning face's outline glows this, so it's unmistakably the one that counts

interface Die {
  group: import("three").Group; start: import("three").Vector3; rest: import("three").Vector3;
  target: import("three").Quaternion; axis1: import("three").Vector3; axis2: import("three").Vector3;
  A1: number; A2: number; h0: number; tc: number; hops: { a: number; b: number; pk: number }[];
  t0: number; dur: number; landed: boolean; fade: number; gone?: boolean;
}

function createEngine(T: THREE, host: HTMLDivElement, getStyle: () => DiceStyle) {
  const renderer = new T.WebGLRenderer({ antialias: true, alpha: true });
  renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
  renderer.shadowMap.enabled = true; renderer.shadowMap.type = T.PCFSoftShadowMap;
  host.appendChild(renderer.domElement);
  const scene = new T.Scene();
  const camera = new T.PerspectiveCamera(38, 1, 0.1, 100); camera.position.set(0, 15.5, 5); camera.lookAt(0, 0, 0);
  // A die settles PRESENTING its winning face square to the viewer (owner: the face that counts wasn't obvious). The
  // result face is turned to look back at the camera, so it's the big, centred, front-facing one — not a top edge.
  const camDir = camera.position.clone().normalize();
  scene.add(new T.HemisphereLight(0xbfd9c4, 0x0a140d, 0.6));
  const key = new T.DirectionalLight(0xfff0d0, 1.15); key.position.set(-6, 14, 6); key.castShadow = true;
  key.shadow.mapSize.set(1024, 1024); key.shadow.camera.near = 1; key.shadow.camera.far = 44;
  key.shadow.camera.left = -12; key.shadow.camera.right = 12; key.shadow.camera.top = 12; key.shadow.camera.bottom = -12; key.shadow.bias = -0.0004;
  scene.add(key);
  const catcher = new T.Mesh(new T.PlaneGeometry(60, 60), new T.ShadowMaterial({ opacity: 0.3 }));
  catcher.rotation.x = -Math.PI / 2; catcher.receiveShadow = true; scene.add(catcher);
  const material = new T.MeshStandardMaterial({ color: 0xd8b45a, metalness: 0.55, roughness: 0.34, emissive: 0x2a1e07, emissiveIntensity: 0.35 });

  const resize = () => { const w = host.clientWidth, h = host.clientHeight; if (!w || !h) return; renderer.setSize(w, h, false); camera.aspect = w / h; camera.updateProjectionMatrix(); };
  resize(); window.addEventListener("resize", resize);

  const texCache = new Map<string, import("three").CanvasTexture>();
  function numberTex(n: string, fill: string, outline: string, hi: boolean) {
    const cacheKey = n + "|" + fill + "|" + outline + "|" + hi; const hit = texCache.get(cacheKey); if (hit) return hit;
    const s = 128, c = document.createElement("canvas"); c.width = c.height = s; const x = c.getContext("2d")!;
    const cx = s / 2, cy = s / 2 + 4;
    x.font = '700 76px Cinzel, Georgia, serif'; x.textAlign = "center"; x.textBaseline = "middle"; x.lineJoin = "round";
    // The outline goes down first (a glow on the winning face), then the fill on top — legible on any body colour.
    if (hi) { x.shadowColor = HI_GLOW; x.shadowBlur = 18; }
    x.strokeStyle = outline; x.lineWidth = hi ? 11 : 7; x.strokeText(n, cx, cy);
    x.shadowBlur = 0;
    x.fillStyle = fill; x.fillText(n, cx, cy);
    if (n === "6" || n === "9") {
      x.strokeStyle = outline; x.lineWidth = hi ? 10 : 8; x.beginPath(); x.moveTo(cx - 24, cy + 36); x.lineTo(cx + 24, cy + 36); x.stroke();
      x.strokeStyle = fill; x.lineWidth = hi ? 6 : 5; x.beginPath(); x.moveTo(cx - 24, cy + 36); x.lineTo(cx + 24, cy + 36); x.stroke();
    }
    const t = new T.CanvasTexture(c); t.anisotropy = 4; texCache.set(cacheKey, t); return t;
  }
  const geometryFor = (s: number) => s === 4 ? new T.TetrahedronGeometry(1.15) : s === 6 ? new T.BoxGeometry(1.5, 1.5, 1.5)
    : s === 8 ? new T.OctahedronGeometry(1.15) : s === 12 ? new T.DodecahedronGeometry(1.12) : new T.IcosahedronGeometry(1.15);
  type RawFace = { centroid: import("three").Vector3; normal: import("three").Vector3; inradius: number };
  function facesOf(geo: import("three").BufferGeometry): RawFace[] {
    const pos = geo.attributes.position!, groups: (RawFace & { pts: V3[] })[] = [];
    const A = new T.Vector3(), B = new T.Vector3(), C = new T.Vector3(), n = new T.Vector3(), ab = new T.Vector3(), cb = new T.Vector3();
    for (let i = 0; i < pos.count; i += 3) {
      A.fromBufferAttribute(pos, i); B.fromBufferAttribute(pos, i + 1); C.fromBufferAttribute(pos, i + 2);
      ab.subVectors(A, B); cb.subVectors(C, B); n.crossVectors(cb, ab).normalize();
      const cen = new T.Vector3().add(A).add(B).add(C).multiplyScalar(1 / 3); if (n.dot(cen) < 0) n.negate();
      let g = groups.find((gg) => gg.normal.dot(n) > 0.9995);
      if (!g) { g = { normal: n.clone(), centroid: new T.Vector3(), inradius: 0, pts: [] }; groups.push(g); }
      g.pts.push(A.clone(), B.clone(), C.clone());
    }
    for (const g of groups) { const c = new T.Vector3(); g.pts.forEach((p) => c.add(p)); g.centroid = c.multiplyScalar(1 / g.pts.length); g.inradius = Math.abs(g.centroid.dot(g.normal)); }
    return groups;
  }
  function d10Faces() {
    const R = 1.0, c = 0.1267, apex = 1.2; const eq: import("three").Vector3[] = [];
    for (let i = 0; i < 10; i++) { const a = i * Math.PI / 5; eq.push(new T.Vector3(Math.cos(a) * R, (i % 2 ? c : -c), Math.sin(a) * R)); }
    const N = new T.Vector3(0, apex, 0), S = new T.Vector3(0, -apex, 0); const pos: number[] = [], faces: RawFace[] = [];
    const tri = (a: import("three").Vector3, b: import("three").Vector3, cc: import("three").Vector3) => {
      const nn = new T.Vector3().subVectors(b, a).cross(new T.Vector3().subVectors(cc, a));
      const cn = new T.Vector3().add(a).add(b).add(cc).multiplyScalar(1 / 3);
      if (nn.dot(cn) < 0) { const t = b; b = cc; cc = t; } pos.push(a.x, a.y, a.z, b.x, b.y, b.z, cc.x, cc.y, cc.z);
    };
    const kite = (Ap: import("three").Vector3, i0: number, i1: number, i2: number) => {
      const P0 = eq[i0]!, P1 = eq[i1]!, P2 = eq[i2]!; tri(Ap, P0, P1); tri(Ap, P1, P2);
      const cen = new T.Vector3().add(Ap).add(P0).add(P1).add(P2).multiplyScalar(0.25);
      const nrm = new T.Vector3().crossVectors(new T.Vector3().subVectors(P1, Ap), new T.Vector3().subVectors(P2, P0)).normalize();
      if (nrm.dot(cen) < 0) nrm.negate(); faces.push({ centroid: cen, normal: nrm, inradius: Math.abs(cen.dot(nrm)) });
    };
    for (let k = 0; k < 5; k++) kite(N, (2 * k + 9) % 10, (2 * k) % 10, (2 * k + 1) % 10);
    for (let k = 0; k < 5; k++) kite(S, (2 * k) % 10, (2 * k + 1) % 10, (2 * k + 2) % 10);
    const geo = new T.BufferGeometry(); geo.setAttribute("position", new T.Float32BufferAttribute(pos, 3)); geo.computeVertexNormals();
    return { geo, faces };
  }
  type Face = { value: number; normal: import("three").Vector3 };
  function makeDie(sides: number, fill: string, outline: string, hiValue: number) {
    let geo: import("three").BufferGeometry, raw: RawFace[], labels: string[], values: number[];
    if (sides === 10 || sides === 100) {
      const d = d10Faces(); geo = d.geo; raw = d.faces;
      if (sides === 100) { labels = ["00", "10", "20", "30", "40", "50", "60", "70", "80", "90"]; values = [0, 10, 20, 30, 40, 50, 60, 70, 80, 90]; }
      else { labels = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "10"]; values = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10]; }
    } else {
      geo = geometryFor(sides);
      // ⚠ BoxGeometry (the d6) is INDEXED — facesOf reads raw position triples, so it must be expanded first or the
      // face triangles (and thus the centroids the numbers sit on) come out wrong. The polyhedra are already non-indexed.
      if (geo.index) geo = geo.toNonIndexed();
      geo.computeVertexNormals(); raw = facesOf(geo).slice(0, sides); labels = []; values = [];
      for (let i = 0; i < sides; i++) { labels.push("" + (i + 1)); values.push(i + 1); }
    }
    const mesh = new T.Mesh(geo, material); mesh.castShadow = true; const group = new T.Group(); group.add(mesh); const fd: Face[] = [];
    raw.forEach((f, i) => {
      const hi = values[i] === hiValue;
      const size = Math.min(1, f.inradius * 1.15) * (sides === 4 ? 1 : 0.9) * (hi ? 1.12 : 1);
      const pl = new T.Mesh(new T.PlaneGeometry(size, size), new T.MeshBasicMaterial({ map: numberTex(labels[i]!, fill, hi ? HI_GLOW : outline, hi), transparent: true, depthWrite: false }));
      pl.position.copy(f.centroid).addScaledVector(f.normal, 0.006); pl.quaternion.setFromUnitVectors(new T.Vector3(0, 0, 1), f.normal); group.add(pl);
      fd.push({ value: values[i]!, normal: f.normal.clone() });
    });
    (group.userData as { faces: Face[]; inradius: number }).faces = fd;
    (group.userData as { faces: Face[]; inradius: number }).inradius = raw[0]!.inradius;
    return group;
  }

  const dice: Die[] = []; let raf = 0, rolling = false, holdUntil = 0;
  const easeOut = (t: number) => 1 - Math.pow(1 - t, 3);
  const heightAt = (d: Die, t: number) => {
    if (t < d.tc) { const f = t / d.tc; return d.h0 * (1 - f * f); }
    for (const h of d.hops) if (t >= h.a && t <= h.b) { const u = (t - h.a) / (h.b - h.a); return h.pk * 4 * u * (1 - u); }
    return 0;
  };
  const _qa = new T.Quaternion(), _qb = new T.Quaternion();
  function frame(now: number) {
    raf = requestAnimationFrame(frame);
    let active = false;
    for (const d of dice) {
      if (d.fade) { const f = Math.min(1, (now - d.fade) / 520); d.group.scale.setScalar(Math.max(0, 1 - f)); if (f >= 1 && !d.gone) { d.gone = true; scene.remove(d.group); } continue; }
      if (d.landed) continue;
      const t = Math.max(0, Math.min(1, (now - d.t0) / d.dur)); if (t <= 0) { active = true; continue; } active = active || t < 1;
      const hx = easeOut(Math.min(1, t / 0.92));
      d.group.position.x = d.start.x + (d.rest.x - d.start.x) * hx; d.group.position.z = d.start.z + (d.rest.z - d.start.z) * hx;
      d.group.position.y = d.rest.y + heightAt(d, t);
      const k = (1 - t) * (1 - t); _qa.setFromAxisAngle(d.axis1, d.A1 * k); _qb.setFromAxisAngle(d.axis2, d.A2 * k); d.group.quaternion.copy(_qa).multiply(_qb).multiply(d.target);
      if (t >= 1) { d.landed = true; d.group.position.copy(d.rest); d.group.quaternion.copy(d.target); }
    }
    if (rolling && !active && dice.every((d) => d.landed || d.fade)) { rolling = false; holdUntil = now + 3200; }
    if (holdUntil && now > holdUntil) { holdUntil = 0; dice.forEach((d) => { if (!d.fade) d.fade = now; }); }
    for (let i = dice.length - 1; i >= 0; i--) if (dice[i]!.gone) dice.splice(i, 1);
    renderer.render(scene, camera);
    if (!dice.length && !holdUntil && !rolling) { cancelAnimationFrame(raf); raf = 0; }
  }

  function doThrow(tw: DiceThrow) {
    resize();
    // A shared throw carries the roller's colour; a local one uses this screen's own.
    const st = (tw.styleId && DICE_STYLES.find((s) => s.id === tw.styleId)) || getStyle();
    material.color.setHex(st.body); material.metalness = st.metal; material.roughness = st.rough; material.emissive.setHex(st.emissive);
    const ink = inkPair(st.body);
    dice.forEach((d) => scene.remove(d.group)); dice.length = 0;
    const n = tw.dice.length, R = n <= 1 ? 0 : Math.min(3.2, 1.1 + n * 0.42);
    tw.dice.slice(0, 40).forEach((entry, idx) => {
      const g = makeDie(entry.sides, ink.fill, ink.outline, entry.value);
      const faces = (g.userData as { faces: Face[]; inradius: number }).faces;
      let pick = faces.findIndex((f) => f.value === entry.value); if (pick < 0) pick = 0;
      const inradius = (g.userData as { faces: Face[]; inradius: number }).inradius;
      const target = new T.Quaternion().setFromUnitVectors(faces[pick]!.normal.clone().normalize(), camDir);
      const ang = n === 1 ? 0 : (idx / n) * Math.PI * 2 + 0.6, rad = n === 1 ? 0 : R * (0.6 + 0.4 * ((idx % 2) ? 1 : 0.7));
      const rest = new T.Vector3(Math.cos(ang) * rad, inradius + 0.3, Math.sin(ang) * rad);
      const start = rest.clone(); start.y += 9 + Math.random() * 3; start.x += (Math.random() - 0.5) * 5; start.z -= 5 + Math.random() * 2;
      const axis1 = new T.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize();
      const axis2 = new T.Vector3(Math.random() - 0.5, Math.random() - 0.5, Math.random() - 0.5).normalize();
      const A1 = (3 + Math.random() * 2) * Math.PI * 2, A2 = (1.2 + Math.random() * 1.6) * Math.PI * 2;
      const tc = 0.17 + Math.random() * 0.05; const peaks: number[] = []; let pk = 1.6 + Math.random() * 0.4; for (let i = 0; i < 5; i++) { peaks.push(pk); pk *= 0.5; }
      const ws = peaks.map((p) => Math.sqrt(p)), wsum = ws.reduce((a, b) => a + b, 0); let cur = tc; const hops: { a: number; b: number; pk: number }[] = [];
      peaks.forEach((p, i) => { const w = (ws[i]! / wsum) * (1 - tc); hops.push({ a: cur, b: cur + w, pk: p }); cur += w; });
      const q0 = new T.Quaternion().setFromAxisAngle(axis1, A1).multiply(new T.Quaternion().setFromAxisAngle(axis2, A2)).multiply(target);
      g.position.copy(start); g.quaternion.copy(q0); scene.add(g);
      dice.push({ group: g, start, rest, target, axis1, axis2, A1, A2, h0: start.y - rest.y, tc, hops, t0: performance.now() + idx * 80, dur: 1300 + Math.random() * 220, landed: false, fade: 0 });
    });
    rolling = true; if (!raf) raf = requestAnimationFrame(frame);
  }

  return {
    throw: doThrow,
    dispose() { cancelAnimationFrame(raf); window.removeEventListener("resize", resize); try { host.removeChild(renderer.domElement); } catch { /* already gone */ } renderer.dispose(); },
  };
}

export function DiceLayer() {
  const ref = useRef<HTMLDivElement>(null);
  // A caption for an INCOMING shared roll — a player who didn't roll it still sees what it was, on any screen.
  const [caption, setCaption] = useState<{ total: number; title: string } | null>(null);
  const capTimer = useRef(0);
  useEffect(() => {
    const el = ref.current; if (!el) return;
    const reduced = typeof matchMedia === "function" && matchMedia("(prefers-reduced-motion: reduce)").matches;
    let disposed = false;
    let engine: ReturnType<typeof createEngine> | null = null;
    let loading = false;
    onDiceThrow((tw) => {
      if (tw.remote) {
        setCaption({ total: tw.total, title: tw.title });
        window.clearTimeout(capTimer.current);
        capTimer.current = window.setTimeout(() => setCaption(null), 3800);
      }
      if (reduced) return;   // no 3D for reduced-motion, but a shared roll's caption still tells them the result
      if (engine) { engine.throw(tw); return; }
      if (loading) return; loading = true;
      import("three").then((T) => {
        if (disposed) return;
        engine = createEngine(T, el, readDiceStyle);
        engine.throw(tw);
      }).catch(() => { /* three failed to load — silently fall back to no animation */ });
    });
    return () => { disposed = true; onDiceThrow(null); window.clearTimeout(capTimer.current); engine?.dispose(); };
  }, []);
  return (
    <div ref={ref} className="dicelayer" aria-hidden="true">
      {caption && <div className="dicecap"><b>{caption.total}</b><span>{caption.title}</span></div>}
    </div>
  );
}
